import FVector from '@data/geometry/FVector.js';

export function mapValue(value: number, min1: number, max1: number, min2: number, max2: number) {
    return min2 + ((value - min1) * (max2 - min2)) / (max1 - min1);
}

/**
 * Maps a value in a range while being robust to floating-point precision issues.
 *
 * If the difference between `min` and `max` is extremely small (within `epsilon`),
 * the function assumes the range is effectively zero and returns the midpoint
 * of the output range to prevent numerical instability.
 *
 * @param v        - Input value to map
 * @param min      - Minimum value of the input range
 * @param max      - Maximum value of the input range
 * @param outMin   - Minimum value of the output range
 * @param outMax   - Maximum value of the output range
 * @param epsilon  - Threshold below which the range is considered zero (default: 1e-4)
 * @returns The mapped value in the output range
 */
export function safeMapValue(v: number, min: number, max: number, outMin: number, outMax: number, epsilon: number = 1e-4): number {
    const range = max - min;

    // If the range is extremely small, treat it as zero to avoid unstable mapping.
    if (Math.abs(range) < epsilon) {
        return (outMin + outMax) / 2;
    }

    // Linear interpolation from [min, max] → [outMin, outMax]
    return ((v - min) / range) * (outMax - outMin) + outMin;
}

////////////////////////////////
// Rays utility:

export function computeDirection(start: FVector, end: FVector) {
    // Assuming start and end are objects with x, y, and z properties
    let direction = new FVector(end.x - start.x, end.y - start.y, end.z - start.z);

    // Return normalized:
    return direction.normalize();
}

export function computeParam1(rayDir: FVector) {
    let param1 = (toDegrees(Math.atan2(rayDir.y, rayDir.x)) + 360) % 360;
    // console.log(param1);
    param1 = Math.round(param1 * 1000) / 1000;
    // console.log(param1);
    return param1;
}

export function computeParam2(rayDir: FVector) {
    rayDir = new FVector(rayDir.x, rayDir.y, rayDir.z);
    let param2 = toDegrees(Math.asin(rayDir.z / rayDir.mag()));
    param2 = Math.round(param2 * 1000) / 1000;
    return param2;
}

export function toDegrees(radians: number) {
    return radians * (180 / Math.PI);
}

// Convert degrees to radians
export function toRadians(degrees: number) {
    return degrees * (Math.PI / 180);
}

export function pause(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
